/**
 * Shared types for the working-hours-schedule feature (DD.9.7.C).
 *
 * A schedule defines weekly open/close windows for a "scope" — the
 * tenant as a whole, a Group, an RbacRole, or an individual User.
 * The phone routing layer consults schedules to decide:
 *
 *   1. Outer destination gate — when an inbound call hits a Group /
 *      Role / user-assigned number, the destination's schedule (or
 *      the tenant fallback) decides whether the call is routed to
 *      the configured fallback (voicemail / AI agent) instead of
 *      ringing anyone.
 *   2. Inner per-rep filter — when ringing a Group / Role's members,
 *      each candidate user's own schedule decides whether to include
 *      them in the ring set.
 *
 * The schedule is purely "when am I open"; the after-hours destination
 * (voicemail vs AI agent) reuses the existing `fallbackKind` /
 * `fallbackAiAgentId` columns on the per-destination config rows
 * (`SoftphoneGroupConfigEntity`, `SoftphoneRoleConfigEntity`,
 * `SoftphoneNumberConfigEntity`). Same shape as the no-answer
 * fallback, so admins set it once.
 */

export type WorkingHoursScopeTypeModel = 'tenant' | 'user' | 'group' | 'role';

/**
 * Scopes use a polymorphic (`scopeType`, `scopeId`) key. Tenant is a
 * singleton scope — there's only one per tenant DB — so it uses
 * `scopeId = TENANT_SCOPE_ID` (0) as a sentinel rather than burning
 * a real FK.
 */
export const TENANT_SCOPE_ID = 0;

/**
 * One open window: `open` and `close` are HH:mm strings in the
 * schedule's own timezone. Comparisons are done lexicographically on
 * the zero-padded HH:mm form, which is correct for 24-hour clocks.
 *
 * `close` is exclusive — a window of `09:00`–`17:00` is "open at
 * 16:59:59, closed at 17:00:00". This matches what humans expect
 * when they say "we close at 5."
 */
export interface WorkingHoursWindowModel {
	open: string;
	close: string;
}

/**
 * Day buckets. Empty array for a day means "closed all day"; multiple
 * windows allow split shifts (e.g. `[{ open: '09:00', close: '12:00' },
 * { open: '13:00', close: '17:00' }]` for a 12-1 lunch break).
 *
 * Day keys are deliberately three-letter lowercase to keep the JSON
 * compact — the schedule is hot-read on every inbound call.
 */
export interface WorkingHoursWeeklyShapeModel {
	sun: ReadonlyArray<WorkingHoursWindowModel>;
	mon: ReadonlyArray<WorkingHoursWindowModel>;
	tue: ReadonlyArray<WorkingHoursWindowModel>;
	wed: ReadonlyArray<WorkingHoursWindowModel>;
	thu: ReadonlyArray<WorkingHoursWindowModel>;
	fri: ReadonlyArray<WorkingHoursWindowModel>;
	sat: ReadonlyArray<WorkingHoursWindowModel>;
}

export const WORKING_HOURS_DAY_KEYS = [
	'sun',
	'mon',
	'tue',
	'wed',
	'thu',
	'fri',
	'sat',
] as const;

export type WorkingHoursDayKey = (typeof WORKING_HOURS_DAY_KEYS)[number];

/**
 * Sensible default for a fresh "9-to-5, M-F, weekends closed" schedule.
 * Used by the admin UI as the seed when an admin first creates a
 * schedule for a new scope.
 */
export const DEFAULT_BUSINESS_HOURS: WorkingHoursWeeklyShapeModel = {
	sun: [],
	mon: [{ open: '09:00', close: '17:00' }],
	tue: [{ open: '09:00', close: '17:00' }],
	wed: [{ open: '09:00', close: '17:00' }],
	thu: [{ open: '09:00', close: '17:00' }],
	fri: [{ open: '09:00', close: '17:00' }],
	sat: [],
};

/**
 * Default tenant timezone when a fresh tenant schedule is created.
 * Picked to match the existing v1 default; admins can change it on
 * the schedule edit form.
 */
export const DEFAULT_SCHEDULE_TIMEZONE = 'America/New_York';
